import { useBackNavigation } from '@hooks'
import { useModuleContext } from '../contexts/module'
import { useCallback, useContext, useEffect, useMemo, useRef } from 'react'
import { useRouter } from 'next/router'
import { PageContext } from '@providers/PageContext'
import { formatGalleryLink } from '@utils'

function useRoomsInformation() {
  const { get, keys } = useBackNavigation()
  const router = useRouter()
  // @ts-ignore
  const {
    dispatch,
    state: { roomsInformation, artworkInformation }
  } = useModuleContext()
  const { isGallery, galleryId, gallerySlug } = artworkInformation || {}

  const {
    page: { pageData: pageMetaInfo }
  } = useContext(PageContext)

  /** Positions in array/data **/
  const positionInRoomArtworksArray = pageMetaInfo?.roomArtworks?.items?.findIndex(({ id }) => id === pageMetaInfo.id)

  // This var stores the current ID
  // It is used to detect when the Artwork changes (look at dispatch at bottom)
  const idRef = useRef(pageMetaInfo.id)
  const positionAsNormalNumber = positionInRoomArtworksArray + 1
  const numberOfArtworksInRoom = pageMetaInfo?.roomArtworks?.total
  const previousArtworkPosition =
    positionInRoomArtworksArray === 0 ? numberOfArtworksInRoom - 1 : positionInRoomArtworksArray - 1
  const nextArtworkPosition =
    positionInRoomArtworksArray === numberOfArtworksInRoom - 1 ? 0 : positionInRoomArtworksArray + 1
  const isDesktopView = typeof window !== 'undefined' && window.innerWidth > 991

  const currentArtworkImageDesktop = isDesktopView
    ? pageMetaInfo.roomArtworks?.items[positionInRoomArtworksArray].croppedHeaderImages[0]
    : pageMetaInfo.roomArtworks?.items[positionInRoomArtworksArray].croppedHeaderImages[2]

  const currentArtworkImage =
    pageMetaInfo.roomArtworks?.items[positionInRoomArtworksArray].croppedHeaderImages.length > 0
      ? currentArtworkImageDesktop
      : pageMetaInfo.roomArtworks?.items[positionInRoomArtworksArray].images[0]

  /** Information about the artworks in room **/
  const previousArtworkId = pageMetaInfo.roomArtworks?.items[previousArtworkPosition].id
  const nextArtworkId = pageMetaInfo.roomArtworks?.items[nextArtworkPosition].id
  const previousArtworkCleanTitle = pageMetaInfo.roomArtworks?.items[previousArtworkPosition].cleanTitle
  const nextArtworkCleanTitle = pageMetaInfo.roomArtworks?.items[nextArtworkPosition].cleanTitle

  /** Links **/
  const previousArtworkLink = `/catalog/artwork/${previousArtworkId}/${previousArtworkCleanTitle}`
  const nextArtworkLink = `/catalog/artwork/${nextArtworkId}/${nextArtworkCleanTitle}`

  const previousArtworkImageDesktop = isDesktopView
    ? pageMetaInfo.roomArtworks?.items[previousArtworkPosition].croppedHeaderImages[0]
    : pageMetaInfo.roomArtworks?.items[previousArtworkPosition].croppedHeaderImages[2]

  const previousArtworkImage =
    pageMetaInfo.roomArtworks?.items[positionInRoomArtworksArray].croppedHeaderImages.length > 0
      ? previousArtworkImageDesktop
      : pageMetaInfo.roomArtworks?.items[previousArtworkPosition].images[0]

  const nextArtworkImageDesktop = isDesktopView
    ? pageMetaInfo.roomArtworks?.items[nextArtworkPosition].croppedHeaderImages[0]
    : pageMetaInfo.roomArtworks?.items[nextArtworkPosition].croppedHeaderImages[2]

  const nextArtworkImage =
    pageMetaInfo.roomArtworks?.items[nextArtworkPosition].croppedHeaderImages.length > 0
      ? nextArtworkImageDesktop
      : pageMetaInfo.roomArtworks?.items[nextArtworkPosition].images[0]
  const nextArtwork = pageMetaInfo.roomArtworks?.items[nextArtworkPosition]

  /**
   * We can come from room, from artworks tab or from internet, if from internet/refresh and is gallery the button
   * brings back to gallery page, any other case, its not shown
   **/
  const backLink = useCallback(() => {
    if (get(keys.COLLECTION.INSIDE_MY_COLLECTION)) {
      // arrived from collection edit
      return {
        href: '/',
        as: get(keys.COLLECTION.INSIDE_MY_COLLECTION),
        scroll: false
      }
    } else if (
      // page refresh inside Artwork
      !get(keys.OVR_INTRO.INSIDE_ROOM) &&
      !get(keys.COLLECTION.INSIDE_MY_COLLECTION) &&
      !get(keys.OVR_INTRO.INSIDE_ARTWORK)
    ) {
      return {
        href: '/',
        as: isGallery ? formatGalleryLink(galleryId, gallerySlug) : '/', // if fails goes to homepage
        scroll: false
      }
    } else if (get(keys.OVR_INTRO.INSIDE_ROOM) && get(keys.OVR_INTRO.INSIDE_ARTWORK)) {
      // inside room artwork
      return {
        href: '/',
        as: get(keys.OVR_INTRO.INSIDE_ARTWORK),
        scroll: false
      }
    } else if (
      get(keys.OVR_INTRO.INSIDE_ARTWORK) &&
      !get(keys.OVR_INTRO.INSIDE_ROOM) &&
      !get(keys.COLLECTION.INSIDE_MY_COLLECTION)
    ) {
      return {
        href: '/',
        as: get(keys.OVR_INTRO.INSIDE_ARTWORK),
        scroll: false
      }
    }
    // all cases failed
    return {
      href: '/',
      as: isGallery ? formatGalleryLink(galleryId, gallerySlug) : '/', // if fails goes to homepage
      scroll: false
    }
  }, [
    galleryId,
    gallerySlug,
    get,
    isGallery,
    keys.COLLECTION.INSIDE_MY_COLLECTION,
    keys.OVR_INTRO.INSIDE_ARTWORK,
    keys.OVR_INTRO.INSIDE_ROOM
  ])

  const data = useMemo(
    () => ({
      positionInRoomArtworksArray,
      positionAsNormalNumber,
      numberOfArtworksInRoom,
      currentArtworkImage,
      pageMetaInfo,
      previousArtworkImage,
      nextArtworkImage,
      nextArtwork,
      previousArtworkLink: {
        href: {
          pathname: '/artwork',
          query: {
            ...router.query
          }
        },
        as: `${previousArtworkLink}${router.query?.comingFromOVRRoom ? '?comingFromOVRRoom=true' : ''}`,
        title: pageMetaInfo.roomArtworks?.items[previousArtworkPosition].displayName
      },
      nextArtworkLink: {
        href: {
          pathname: '/artwork',
          query: {
            ...router.query
          }
        },
        as: `${nextArtworkLink}${router.query?.comingFromOVRRoom ? '?comingFromOVRRoom=true' : ''}`,
        title: pageMetaInfo.roomArtworks?.items[nextArtworkPosition].displayName
      },
      backLink
    }),
    [
      backLink,
      currentArtworkImage,
      nextArtwork,
      nextArtworkImage,
      nextArtworkLink,
      nextArtworkPosition,
      numberOfArtworksInRoom,
      pageMetaInfo,
      positionAsNormalNumber,
      positionInRoomArtworksArray,
      previousArtworkImage,
      previousArtworkLink,
      previousArtworkPosition,
      router.query
    ]
  )

  useEffect(() => {
    if (!roomsInformation || idRef.current !== pageMetaInfo.id) {
      idRef.current = pageMetaInfo.id
      dispatch({
        type: 'SET_ROOMSINFO',
        payload: data
      })
    }
  }, [data, dispatch, pageMetaInfo.id, roomsInformation])
}

export default useRoomsInformation
